import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { app } from '../firebaseConfig';
import { reportClientError } from './ErrorReporter';

const functions = getFunctions(app, 'us-central1');
const auth = getAuth();

const clearLocalSession = (pageId) => {
  sessionStorage.removeItem(`admin_token_${pageId}`);
  sessionStorage.removeItem(`admin_expires_${pageId}`);
};

const normalizeAdminError = (pageId, error) => {
  if (
    error.code === 'functions/permission-denied' &&
    String(error.message || '').toLowerCase().includes('session')
  ) {
    clearLocalSession(pageId);
    const sessionError = new Error('Admin session expired. Please log in again.');
    sessionError.code = error.code;
    return sessionError;
  }
  if (
    error.code === 'functions/not-found' &&
    String(error.message || '').toLowerCase().includes('guestbook module')
  ) {
    const saveFirstError = new Error('Save this gift as a draft first, then copy the Blessing Wall invite link.');
    saveFirstError.code = error.code;
    return saveFirstError;
  }
  return error;
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const AdminAuthService = {
  // --- Auth Actions ---
  
  verifyAdminPassword: async (pageId, password) => {
    const verify = httpsCallable(functions, 'verifyAdminPassword');
    try {
      const result = await verify({ pageId, password });
      return result.data;
    } catch (error) {
      if (error.code === 'functions/internal') {
        await wait(700);
        try {
          const retryResult = await verify({ pageId, password });
          return retryResult.data;
        } catch (retryError) {
          reportClientError('admin.verify.retry', retryError);
          throw retryError;
        }
      }
      reportClientError('admin.verify', error);
      throw error;
    }
  },

  establishSession: async (pageId, sessionData) => {
    const { adminSessionToken, customToken, expiresAt } = sessionData;
    
    // 1. Sign in to Firebase Auth with Custom Token
    if (customToken) {
      await signInWithCustomToken(auth, customToken);
    }

    // 2. Store session in sessionStorage
    sessionStorage.setItem(`admin_token_${pageId}`, adminSessionToken);
    sessionStorage.setItem(`admin_expires_${pageId}`, expiresAt);
  },

  changeAdminPassword: async (pageId, oldPassword, newPassword) => {
    const change = httpsCallable(functions, 'changeAdminPassword');
    try {
      const result = await change({ pageId, currentPassword: oldPassword, newPassword });
      return result.data;
    } catch (error) {
      reportClientError('admin.changePassword', error);
      throw error;
    }
  },

  // --- Page Write Actions (Moved from direct SDK) ---

  savePage: async (pageId, pageData, options = {}) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const save = httpsCallable(functions, 'savePage');
    try {
      const result = await save({
        pageId,
        adminSessionToken: token,
        pageData,
        cleanupUnusedMedia: options.cleanupUnusedMedia === true,
      });
      return result.data;
    } catch (error) {
      reportClientError('admin.savePage', error);
      throw normalizeAdminError(pageId, error);
    }
  },

  deletePage: async (pageId) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const del = httpsCallable(functions, 'deletePage');
    try {
      const result = await del({ pageId, adminSessionToken: token });
      return result.data;
    } catch (error) {
      reportClientError('admin.deletePage', error);
      throw normalizeAdminError(pageId, error);
    }
  },

  lockPage: async (pageId) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const lock = httpsCallable(functions, 'lockPage');
    try {
      const result = await lock({ pageId, adminSessionToken: token });
      return result.data;
    } catch (error) {
      reportClientError('admin.lockPage', error);
      throw normalizeAdminError(pageId, error);
    }
  },

  moderateGuestMessage: async (pageId, messageId, status) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const moderate = httpsCallable(functions, 'moderateGuestMessage');
    try {
      const result = await moderate({ pageId, messageId, status, adminSessionToken: token });
      return result.data;
    } catch (error) {
      reportClientError('admin.moderateGuestMessage', error);
      throw normalizeAdminError(pageId, error);
    }
  },

  createGuestbookInvite: async (pageId, moduleId) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const createInvite = httpsCallable(functions, 'createGuestbookInvite');
    try {
      const result = await createInvite({ pageId, moduleId, adminSessionToken: token });
      return result.data;
    } catch (error) {
      reportClientError('admin.createGuestbookInvite', error);
      throw normalizeAdminError(pageId, error);
    }
  },

  createUploadTicket: async (pageId, mediaType) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const ticket = httpsCallable(functions, 'createUploadTicket');
    try {
      const result = await ticket({ pageId, adminSessionToken: token, mediaType });
      if (result.data?.customToken) {
        await signInWithCustomToken(auth, result.data.customToken);
        await auth.currentUser?.getIdToken(true);
      }
      return result.data;
    } catch (error) {
      reportClientError('admin.createUploadTicket', error);
      throw normalizeAdminError(pageId, error);
    }
  },

  createGuestUploadTicket: async (pageId, moduleId) => {
    const ticket = httpsCallable(functions, 'createGuestUploadTicket');
    try {
      const result = await ticket({ pageId, moduleId });
      return result.data;
    } catch (error) {
      reportClientError('admin.createGuestUploadTicket', error);
      throw error;
    }
  },

  // --- Session Helpers ---

  isAdminAuthenticated: (pageId) => {
    const token = sessionStorage.getItem(`admin_token_${pageId}`);
    const expires = sessionStorage.getItem(`admin_expires_${pageId}`);
    if (!token || !expires) return false;
    
    if (Number(expires) < Date.now()) {
      AdminAuthService.logoutAdmin(pageId);
      return false;
    }
    return true;
  },

  getAdminToken: (pageId) => {
    return sessionStorage.getItem(`admin_token_${pageId}`);
  },

  logoutAdmin: async (pageId) => {
    clearLocalSession(pageId);
    await signOut(auth);
  }
};

export default AdminAuthService;
